import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';

const CorporateHeader = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDivisionsOpen, setIsDivisionsOpen] = useState(false);
  const [isAIServicesOpen, setIsAIServicesOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const divisions = [
    { name: 'Mindset Shift Media', path: '/divisions/mindset-shift-media' },
    { name: 'Author Growth Systems', path: '/divisions/author-growth-systems' },
    { name: 'Publishing & Ghostwriting', path: '/divisions/publishing-ghostwriting' },
    { name: 'Brand Infrastructure', path: '/divisions/brand-infrastructure' },
    { name: 'Business Directory', path: '/divisions/business-directory' },
  ];

  const aiServices = [
    { name: "Mother's Day Frames", path: '/mothers-day-frames' },
    { name: 'AI Luxury Photoshoots', path: '/ai-photoshoots' },
    { name: 'AI Flyer Design', path: '/ai-flyers' },
  ];

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Start', path: '/start' },
    { name: 'Divisions', path: null, hasDropdown: 'divisions' },
    { name: 'AI Services', path: null, hasDropdown: 'ai-services' },
    { name: 'Store', path: '/store' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'Start a Project', path: '/start-project' },
  ];

  const handleNavClick = (item) => {
    if (item.path) {
      navigate(item.path);
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-black/95 backdrop-blur-md shadow-2xl' : 'bg-black/80 backdrop-blur-sm'
    }`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_4ba17392-007a-438c-b296-76e166f0f0cf/artifacts/67pzcjdk_eBlackBookListings.png" 
              alt="eBlackBookListings LLC"
              className="h-10 w-auto"
            />
            <div className="hidden sm:block">
              <span className="text-white font-bold text-lg tracking-tight">eBlackBookListings</span>
              <span className="text-gray-400 text-xs block -mt-1">LLC</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => (
              item.hasDropdown ? (
                <div 
                  key={item.name}
                  className="relative"
                  onMouseEnter={() => item.hasDropdown === 'divisions' ? setIsDivisionsOpen(true) : setIsAIServicesOpen(true)}
                  onMouseLeave={() => item.hasDropdown === 'divisions' ? setIsDivisionsOpen(false) : setIsAIServicesOpen(false)}
                >
                  <button 
                    className="flex items-center gap-1 px-4 py-2 text-gray-300 hover:text-white transition-colors duration-200 font-medium"
                  >
                    {item.name}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                      (item.hasDropdown === 'divisions' ? isDivisionsOpen : isAIServicesOpen) ? 'rotate-180' : ''
                    }`} />
                  </button>
                  
                  <div className={`absolute top-full left-0 w-64 bg-black/95 backdrop-blur-md border border-gray-800 rounded-lg shadow-2xl overflow-hidden transition-all duration-200 ${
                    (item.hasDropdown === 'divisions' ? isDivisionsOpen : isAIServicesOpen) ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                  }`}>
                    <div className="py-2">
                      {(item.hasDropdown === 'divisions' ? divisions : aiServices).map((sub) => (
                        <Link
                          key={sub.name}
                          to={sub.path}
                          className="block px-4 py-3 text-gray-300 hover:text-white hover:bg-red-600/20 transition-all duration-200 border-l-2 border-transparent hover:border-red-600"
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`px-4 py-2 font-medium transition-colors duration-200 ${
                    location.pathname === item.path 
                      ? 'text-red-500' 
                      : 'text-gray-300 hover:text-white'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>

          {/* CTA Button - Desktop */}
          <div className="hidden lg:block">
            <Button 
              onClick={() => navigate('/start-project')}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 py-2 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-red-600/30"
            >
              Work With Us
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div className={`lg:hidden fixed inset-x-0 top-20 bg-black/98 backdrop-blur-md border-t border-gray-800 transition-all duration-300 ${
        isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
      }`}>
        <div className="container mx-auto px-4 py-6">
          <nav className="space-y-1">
            {navItems.map((item) => (
              item.hasDropdown ? (
                <div key={item.name} className="space-y-1">
                  <button
                    onClick={() => item.hasDropdown === 'divisions' ? setIsDivisionsOpen(!isDivisionsOpen) : setIsAIServicesOpen(!isAIServicesOpen)}
                    className="w-full flex items-center justify-between px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-lg transition-all duration-200"
                  >
                    {item.name}
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${
                      (item.hasDropdown === 'divisions' ? isDivisionsOpen : isAIServicesOpen) ? 'rotate-180' : ''
                    }`} />
                  </button>
                  
                  {(item.hasDropdown === 'divisions' ? isDivisionsOpen : isAIServicesOpen) && (
                    <div className="pl-4 space-y-1">
                      {(item.hasDropdown === 'divisions' ? divisions : aiServices).map((sub) => (
                        <Link
                          key={sub.name}
                          to={sub.path}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className="block px-4 py-2 text-sm text-gray-400 hover:text-white hover:bg-red-600/20 rounded-lg transition-all duration-200"
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg transition-all duration-200 ${
                    location.pathname === item.path 
                      ? 'text-red-500 bg-red-600/10' 
                      : 'text-gray-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </nav>
          
          <div className="mt-6 pt-6 border-t border-gray-800">
            <Button 
              onClick={() => {
                navigate('/start-project');
                setIsMobileMenuOpen(false);
              }}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg"
            >
              Work With Us
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default CorporateHeader;
